import { useCallback, useRef, useState } from "react";
import { toast } from "react-toastify";

import { CLONE_TYPE_WELL_ERROR_MESSAGE } from "constants/errorMessages.constants";
import {
  selectHasWellDataError,
  selectSelectedTypeWell,
  useForecastStore
} from "stores/forecast";

import { useTypeWellSaver, useUndoRedo } from "components/arps/hooks";
import { useFetchForecastArray } from "components/arps/hooks/useFetchForecastArray";

interface UseTypeWellSaveAsParams {
  selectedProject?: { projectId: string };
  saveAsToProjectId: string;
  saveAsTitle: string;
  onSuccess: () => void;
}

export function useTypeWellSaveAs({
  selectedProject,
  saveAsToProjectId,
  saveAsTitle,
  onSuccess
}: UseTypeWellSaveAsParams) {
  const undoRedoManager = useUndoRedo();
  const { onCloneTypeWell } = useTypeWellSaver(undoRedoManager);

  const [isCloning, setIsCloning] = useState(false);

  const selectedTypeWell = useForecastStore(selectSelectedTypeWell);
  const fsSetSelectedTypeWell = useForecastStore((s) => s.setSelectedTypeWell);

  // Fetch forecast array for the selected type well
  const { data: forecastArrayData } = useFetchForecastArray({
    ids: selectedTypeWell?.id ? [selectedTypeWell.id] : []
  });

  const hasWellDataError = useForecastStore(selectHasWellDataError);

  const originalProject = useRef<string>(selectedProject?.projectId);
  const originalSelectedTypeWell = useRef(selectedTypeWell);

  // Update refs when selectedTypeWell changes
  const updateOriginalRefs = useCallback(() => {
    originalSelectedTypeWell.current = selectedTypeWell;
    originalProject.current = selectedProject?.projectId;
  }, [selectedTypeWell, selectedProject?.projectId]);

  const handleSave = useCallback(async () => {
    const moveInput =
      saveAsToProjectId !== originalProject.current
        ? { newProjectId: saveAsToProjectId }
        : null;

    setIsCloning(true);

    try {
      // Get forecast array from the already-fetched data
      const forecastArray = selectedTypeWell?.id
        ? forecastArrayData?.forecastArray?.[selectedTypeWell.id]
        : undefined;

      await onCloneTypeWell(saveAsTitle, moveInput, forecastArray);

      // Restore original selectedTypeWell
      if (originalSelectedTypeWell.current) {
        fsSetSelectedTypeWell(originalSelectedTypeWell.current);
      }

      onSuccess();
    } catch (_) {
      // Always show generic error message to avoid exposing technical details
      toast.error(CLONE_TYPE_WELL_ERROR_MESSAGE);
    } finally {
      setIsCloning(false);
    }
  }, [
    saveAsTitle,
    saveAsToProjectId,
    onCloneTypeWell,
    fsSetSelectedTypeWell,
    onSuccess,
    selectedTypeWell,
    forecastArrayData
  ]);

  return {
    handleSave,
    isCloning,
    hasWellDataError,
    updateOriginalRefs
  };
}
